/* ============================================================
   ARNY ROSS — Motion Layer
   Vanilla IntersectionObserver scroll-reveal + hero entrance.
   No frameworks, no dependencies. Namespaced so it does not
   collide with the inline lookbook-filter script.

   Contract with base.css.liquid:
   - Reveal groups get class `ar-stagger`; each direct child gets
     a `--ar-i` index that CSS turns into a cascade delay.
   - Standalone reveal targets get class `ar-reveal`.
   - When a target scrolls into view we add `ar-in` (CSS reveals it).
   - The hero fires its entrance via `.hero.is-loaded`.

   If the visitor prefers reduced motion, this script bails and the
   page stays in its original static state (nothing is ever hidden,
   because the hidden states in CSS are gated behind html.ar-motion,
   which the head guard only adds for motion-OK visitors).
   ============================================================ */
(function () {
  var root = document.documentElement;

  // Respect reduced motion — leave the page exactly as it was.
  try {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
  } catch (e) {
    return;
  }

  // Signal the head-guard failsafe that the motion script is alive,
  // so it will not force-reveal everything after its timeout.
  root.setAttribute('data-ar-ready', '1');

  // Containers whose direct children cascade in, one after another.
  var STAGGER_SELECTORS = [
    '.section-header',
    '.founder-content',
    '.sourcing-content',
    '.trust-edu-grid > div:first-child',
    '.trust-list',
    '.featured-grid',
    '.lookbook-grid',
    '.testimonials-grid',
    '.community-stats',
    '.founder-stats',
    '.sourcing-flags',
    '.contact-grid > div',
    '.faith',
    // Reviews page + Lives archive (added with those features)
    '.reviews-grid',
    '.lives-grid',
    '.live-lead',
    '.live-screens',
    '.live-foot',
    // Trust badges strip
    '.trust-badges-row'
  ];

  // Elements revealed individually — each fires as IT scrolls into view,
  // instead of cascading together as a group. Used for the mission pillars
  // so each slides in from the left one-by-one as you scroll.
  var REVEAL_SELECTORS = [
    '.pillar'
  ];

  var observer = null;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(function (entries, obs) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (entry.isIntersecting) {
          entry.target.classList.add('ar-in');
          obs.unobserve(entry.target); // reveal once, then stop watching
        }
      }
    }, {
      threshold: 0.15,
      rootMargin: '0px 0px -8% 0px'
    });
  }

  function prepStagger(scope) {
    for (var s = 0; s < STAGGER_SELECTORS.length; s++) {
      var groups = scope.querySelectorAll(STAGGER_SELECTORS[s]);
      for (var g = 0; g < groups.length; g++) {
        var group = groups[g];
        if (group.classList.contains('ar-stagger')) continue; // already prepped
        group.classList.add('ar-stagger');

        var kids = group.children;
        for (var k = 0; k < kids.length; k++) {
          kids[k].style.setProperty('--ar-i', k);
        }

        if (observer) {
          observer.observe(group);
        } else {
          group.classList.add('ar-in'); // no IO support: reveal immediately
        }
      }
    }
  }

  function prepReveal(scope) {
    for (var s = 0; s < REVEAL_SELECTORS.length; s++) {
      var items = scope.querySelectorAll(REVEAL_SELECTORS[s]);
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (item.classList.contains('ar-reveal')) continue; // already prepped
        item.classList.add('ar-reveal');
        item.style.setProperty('--ar-i', i); // index → small cascade delay in CSS
        if (observer) {
          observer.observe(item);
        } else {
          item.classList.add('ar-in'); // no IO support: reveal immediately
        }
      }
    }
  }

  function fireHero(scope) {
    var hero = scope.querySelector('.hero');
    if (!hero) return;
    // Two frames so the initial hidden state paints before we release it.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        hero.classList.add('is-loaded');
      });
    });
  }

  function init(scope) {
    scope = scope || document;
    prepStagger(scope);
    prepReveal(scope);
    fireHero(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Shopify theme editor: re-run for a section that just re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
})();
